import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

import { SessionService } from 'src/app/core/services/session.service';
import { getUserUtcOffset } from 'src/app/shared/utils/helpers';
import { UiModule } from 'src/app/ui/ui.module';
import { MfaEnrolmentWizardComponent } from 'src/app/features/settings/components/security-page/mfa-enrolment-wizard/mfa-enrolment-wizard.component';
import { MfaMethod, MfaStatus } from 'src/app/features/settings/models/mfa.models';

export interface MfaEnrolmentPromptData {
  status: MfaStatus;
}

/**
 * Set up a second factor, offered right after signing in.
 *
 * Shown to people who owe a factor and have none: the alternative is hearing
 * about it the day the grace window closes, when the login is refused and
 * enrolling needs the session they can no longer get.
 *
 * Standalone so the effect that opens it does not have to wait for the lazy
 * settings module.
 */
@Component({
  selector: 'app-mfa-enrolment-prompt',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    UiModule,
    MfaEnrolmentWizardComponent,
  ],
  templateUrl: './mfa-enrolment-prompt.component.html',
  styleUrls: ['./mfa-enrolment-prompt.component.scss'],
})
export class MfaEnrolmentPromptComponent {
  readonly status: MfaStatus;

  /** Null until a method is chosen, which is what mounts the wizard. */
  method: MfaMethod | null = null;
  enrolled = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: MfaEnrolmentPromptData,
    private readonly dialogRef: MatDialogRef<MfaEnrolmentPromptComponent, boolean>,
    private readonly session: SessionService
  ) {
    this.status = data.status;
  }

  /**
   * Whether this can be put off.
   *
   * True for anyone the policy does not oblige, and for anyone still inside
   * their window. A user who owes a factor past their window never reaches a
   * session at all -- the login is refused before one is issued -- so the
   * unskippable branch is a guard against a policy that changes underneath a
   * live session, not the ordinary path.
   */
  get skippable(): boolean {
    return !this.status.required || !!this.status.in_grace_period;
  }

  /** The deadline in the reader's own zone. The backend sends naive UTC, and
   *  `AdaptToUserUtcOffsetPipe` is not standalone, so the shift happens here
   *  and the template only formats. */
  get deadline(): Date | null {
    const raw = this.status.enrolment_deadline;
    if (!raw) {
      return null;
    }
    const shifted = new Date(raw);
    if (Number.isNaN(shifted.getTime())) {
      return null;
    }
    // Minutes, not hours: half-hour zones give a fractional offset.
    shifted.setMinutes(
      shifted.getMinutes() + Math.round((getUserUtcOffset() ?? 0) * 60)
    );
    return shifted;
  }

  get emailAvailable(): boolean {
    return !!this.status.email_otp_available;
  }

  choose(method: MfaMethod): void {
    this.method = method;
  }

  /** Back to the choice, not out of the dialog: nothing is enrolled yet. */
  onCancelled(): void {
    this.method = null;
  }

  onEnrolled(): void {
    this.enrolled = true;
  }

  close(): void {
    this.dialogRef.close(this.enrolled);
  }

  /**
   * The way out when there is no skip.
   *
   * Without it a failed `enroll` leaves the reader inside a dialog that has no
   * ×, no skip and no way back to the app -- only a page reload. Signing out
   * is the honest escape: they owe a factor and cannot be in here without one.
   * Closes first, or the dialog floats over the login page it lands on.
   */
  logout(): void {
    this.dialogRef.close(this.enrolled);
    this.session.logout();
  }
}
